import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';
import { useLazyGetContractorQuery } from '@store/rtk/contractors';
import { useLazyGetMemberDetailsQuery } from '@store/rtk/members';
import { useLazyGetPayorsQuery } from '@store/rtk/payors';
import { selectContractor } from '@store/slices/contractors/contractorSlice';
import { selectMembersInfo } from '@store/slices/members/info/membersInfoSlice';
import { selectPayorsInfo } from '@store/slices/payors/info/payorsInfoSlice';
import { selectUsers } from '@store/slices/users/usersSlice';
import { X_HEADER_ID_NAME } from '@utils/constants';
import { decrypt, sessionSetter } from '@utils/storage';
import { CONTRACTOR_USER } from '@routes/helpers';

type EntityType = 'contractor' | 'member' | 'payor';

type EntityConfig = {
  fallbackName: string;
  name?: string;
  requestName: () => Promise<string>;
};

const PATH_ENTITY_MAP: Record<string, EntityType> = {
  contractors: 'contractor',
  members: 'member',
  payors: 'payor',
};

export const useBreadcrumbEntityName = (): string | undefined => {
  const { pathname } = useLocation();
  const params = useParams();
  const { contractorInfo } = useAppSelector(selectContractor);
  const { membersData } = useAppSelector(selectMembersInfo);
  const payorsInfo = useAppSelector(selectPayorsInfo);
  const { userType } = useAppSelector(selectUsers);
  const [getContractor] = useLazyGetContractorQuery();
  const [getMemberDetails] = useLazyGetMemberDetailsQuery();
  const [getPayor] = useLazyGetPayorsQuery();
  const requestedEntityRef = useRef('');
  const [fetchedEntity, setFetchedEntity] = useState<{
    key: string;
    name: string;
  } | null>(null);

  const pathSegments = useMemo(
    () => pathname.split('/').filter(Boolean),
    [pathname]
  );
  const entityType = PATH_ENTITY_MAP[pathSegments[0]];
  const isCreateRoute = pathSegments[1] === 'create';
  const isContractorPortalEntity =
    userType === CONTRACTOR_USER && entityType === 'contractor';

  const entityId = useMemo(() => {
    if (!entityType || isCreateRoute) return undefined;

    const encryptedId = params.id ?? pathSegments[2];
    const decryptedId = decrypt(encryptedId)?.[entityType];
    const headerId =
      sessionSetter.getItem<Record<string, unknown>>(X_HEADER_ID_NAME)?.[
        entityType
      ];

    return decryptedId ?? headerId;
  }, [entityType, isCreateRoute, params.id, pathSegments]);

  const entityConfig = useMemo<Record<EntityType, EntityConfig>>(
    () => ({
      contractor: {
        fallbackName: 'Contractor',
        name: contractorInfo?.name,
        requestName: async () => {
          const contractor = await getContractor({}).unwrap();
          document.title = `Contractor - ${contractor?.name}`;
          return contractor?.name ?? '';
        },
      },
      member: {
        fallbackName: 'Member',
        name:
          membersData?.fullName ||
          [membersData?.firstName, membersData?.lastName]
            .filter(Boolean)
            .join(' '),
        requestName: async () => {
          const member = await getMemberDetails({}).unwrap();
          return (
            member?.fullName ||
            [member?.firstName, member?.lastName].filter(Boolean).join(' ')
          );
        },
      },
      payor: {
        fallbackName: 'Payor',
        name: payorsInfo?.info?.companyName,
        requestName: async () => {
          const payor = await getPayor({}).unwrap();
          return payor?.companyName ?? '';
        },
      },
    }),
    [
      getContractor,
      getMemberDetails,
      getPayor,
      contractorInfo?.name,
      membersData?.firstName,
      membersData?.fullName,
      membersData?.lastName,
      payorsInfo?.info?.companyName,
    ]
  );

  useEffect(() => {
    if (!entityType || !entityId || isCreateRoute || isContractorPortalEntity)
      return;

    const entityKey = `${entityType}:${entityId}`;
    if (requestedEntityRef.current === entityKey) return;

    requestedEntityRef.current = entityKey;
    setFetchedEntity(null);

    entityConfig[entityType]
      .requestName()
      .then((name) => {
        setFetchedEntity({ key: entityKey, name });
      })
      .catch(() => {
        setFetchedEntity({ key: entityKey, name: '' });
      });
  }, [
    entityConfig,
    entityId,
    entityType,
    isCreateRoute,
    isContractorPortalEntity,
  ]);

  if (!entityType || isCreateRoute) return undefined;
  if (isContractorPortalEntity) return contractorInfo?.name || 'Contractor';

  const config = entityConfig[entityType];
  if (!entityId) return config.name;

  const entityKey = `${entityType}:${entityId}`;
  if (fetchedEntity?.key === entityKey && fetchedEntity.name) {
    return config.name || fetchedEntity.name;
  }

  return config.fallbackName;
};
